import { useEffect, useState } from 'react';
import { AlertTriangle, Download, HardDrive, WifiOff, X } from 'lucide-react';
import { getDesktopRelease, requestDesktopReleaseDownload } from '../../services/api';
import styles from './DesktopDownloadDialog.module.css';

function formatBytes(value) {
  const bytes = Number(value) || 0;
  if (bytes < 1024 ** 2) return `${(bytes / 1024).toFixed(1)} KB`;
  if (bytes < 1024 ** 3) return `${(bytes / 1024 ** 2).toFixed(1)} MB`;
  return `${(bytes / 1024 ** 3).toFixed(2)} GB`;
}

export default function DesktopDownloadDialog({ onClose }) {
  const [release, setRelease] = useState(null);
  const [acknowledged, setAcknowledged] = useState(false);
  const [loading, setLoading] = useState(true);
  const [downloading, setDownloading] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    let active = true;
    getDesktopRelease()
      .then(value => { if (active) setRelease(value); })
      .catch(reason => { if (active) setError(reason.message); })
      .finally(() => { if (active) setLoading(false); });
    return () => { active = false; };
  }, []);

  useEffect(() => {
    const onKeyDown = (event) => {
      if (event.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, [onClose]);

  const startDownload = async () => {
    setDownloading(true);
    setError('');
    try {
      const grant = await requestDesktopReleaseDownload();
      window.location.assign(grant.download_url);
    } catch (reason) {
      setError(reason.message);
      setDownloading(false);
    }
  };

  return (
    <div className={styles.backdrop} onMouseDown={event => {
      if (event.target === event.currentTarget) onClose();
    }}>
      <section
        className={styles.dialog}
        role="dialog"
        aria-modal="true"
        aria-labelledby="desktop-download-title"
      >
        <header className={styles.header}>
          <div>
            <span className={styles.eyebrow}>WINDOWS OFFLINE APP <b>BETA</b></span>
            <h2 id="desktop-download-title">Track Day Analysis for Windows</h2>
            <p>Analyze at the track without internet, then sync when you have a faster connection.</p>
          </div>
          <button className={styles.close} onClick={onClose} aria-label="Close">
            <X size={18} aria-hidden="true" />
          </button>
        </header>

        <div className={styles.benefits}>
          <div>
            <WifiOff size={20} aria-hidden="true" />
            <span><strong>Work offline</strong>Process telemetry, previews, overlays, and video locally.</span>
          </div>
          <div>
            <HardDrive size={20} aria-hidden="true" />
            <span><strong>Sync later</strong>Upload telemetry, rendered video, or a full source archive.</span>
          </div>
        </div>

        <div className={styles.steps}>
          <h3>Getting started</h3>
          <ol>
            <li>
              Click <strong>Download for Windows</strong>. When the download finishes, open the
              Track Day Analysis setup file from your browser’s Downloads list and follow the
              installation steps.
            </li>
            <li>
              At the end of installation, leave <strong>Launch Track Day Analysis</strong> selected.
              For future use, launch it from the Windows Start menu or desktop shortcut.
            </li>
            <li>
              Track Day Analysis opens in your regular web browser and works much like the hosted
              website. The browser connects to the application running on your own computer, so
              normal local analysis does not require internet access.
            </li>
            <li>
              Import and analyze your telemetry and video normally. Closing the browser tab does
              not shut down the local application.
            </li>
            <li>
              When you have a faster internet connection, use <strong>Session → Server Sync</strong>
              {' '}or <strong>Sync all</strong> to upload selected sessions to your hosted account.
            </li>
            <li>
              To shut down the application, find the TDA icon in the Windows notification area—
              sometimes under <strong>Show hidden icons</strong> (^). Right-click it and select
              {' '}<strong>Exit Track Day Analysis</strong>.
            </li>
          </ol>
        </div>

        <div className={styles.warning}>
          <AlertTriangle size={21} aria-hidden="true" />
          <div>
            <strong>Unsigned beta software</strong>
            <p>
              This beta installer is not digitally signed. Windows SmartScreen or antivirus
              software may warn you, quarantine files, or prevent installation. Only install it
              if you downloaded it directly from trackdayanalysis.com and are comfortable testing
              beta software. Do not disable your antivirus software.
            </p>
          </div>
        </div>

        {loading && <p className={styles.status}>Checking the current Windows release…</p>}
        {!loading && release?.available && (
          <div className={styles.release}>
            <div>
              <span>Version</span>
              <strong>{release.version}</strong>
            </div>
            <div>
              <span>Installer size</span>
              <strong>{formatBytes(release.size_bytes)}</strong>
            </div>
            <div>
              <span>Released</span>
              <strong>{release.release_date}</strong>
            </div>
            {release.sha256 && (
              <div className={styles.checksum}>
                <span>SHA-256</span>
                <code title={release.sha256}>{release.sha256}</code>
              </div>
            )}
          </div>
        )}
        {!loading && release && !release.available && (
          <p className={styles.unavailable}>{release.message}</p>
        )}
        {error && <p className={styles.error}>⚠ {error}</p>}

        {release?.available && (
          <label className={styles.acknowledgment}>
            <input
              type="checkbox"
              checked={acknowledged}
              onChange={event => setAcknowledged(event.target.checked)}
            />
            <span>I understand this beta is unsigned and may be blocked by security software.</span>
          </label>
        )}

        <footer className={styles.actions}>
          <button className={styles.secondary} onClick={onClose}>Cancel</button>
          <button
            className={styles.primary}
            disabled={!acknowledged || !release?.available || loading || downloading}
            onClick={startDownload}
          >
            <Download size={16} aria-hidden="true" />
            {downloading ? 'Starting download…' : 'Download for Windows'}
          </button>
        </footer>
      </section>
    </div>
  );
}
